import React, { useState, useEffect, useRef } from 'react';
import { getDrivers, createDriver, updateDriver, getDevices } from '../traccar';
import { getDriverPhoto, saveDriverPhoto, getDriverDocs, saveDriverDocs, fileToResizedDataUrl, fileToDataUrl } from '../photoStore';
import { playOpen, playClose } from '../clickSound';
import VehicleHistory from '../VehicleHistory';

const TABS = ['Info', 'Employment', 'Payments', 'Units & Logs', 'Documents'];

const inputStyle = { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', background: '#ffffff' };
const lbl = { fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '3px' };
const btnBlue = { padding: '8px 16px', borderRadius: '6px', fontWeight: 700, fontSize: '13px', background: 'linear-gradient(135deg, #0066ff, #0047d0)', color: 'white' };
const btnOrange = { ...btnBlue, background: 'linear-gradient(135deg, #f97316, #f4511e)' };

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : 'current');

// ---- live camera capture (uses the computer's webcam) ----
function CameraModal({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    navigator.mediaDevices.getUserMedia({ video: { width: 1280, height: 720, facingMode: 'user' } })
      .then((stream) => {
        if (!alive) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch((e) => setErr(
        e.name === 'NotAllowedError'
          ? 'Camera permission was denied. Allow camera access for this site in the browser settings.'
          : 'No camera found on this computer.'
      ));
    return () => {
      alive = false;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const snap = () => {
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    const scale = Math.min(1, 800 / video.videoWidth);
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    onCapture(canvas.toDataURL('image/jpeg', 0.85));
    onClose();
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', padding: '20px', maxWidth: '640px', width: '100%', textAlign: 'center' }}>
        <h3 style={{ margin: '0 0 12px', fontFamily: 'Poppins, sans-serif', fontSize: '16px' }}>📸 Take a photo</h3>
        {err
          ? <p style={{ color: '#b91c1c', fontSize: '14px', padding: '30px 10px' }}>{err}</p>
          : <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', borderRadius: '10px', background: '#0f172a' }} />}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '14px' }}>
          <button style={{ background: '#e2e8f0', color: '#334155', padding: '9px 18px', borderRadius: '8px', fontWeight: 600 }} onClick={onClose}>Cancel</button>
          {!err && <button style={{ ...btnBlue, padding: '9px 22px' }} onClick={snap}>📷 Capture</button>}
        </div>
      </div>
    </div>
  );
}

export default function DriversPage() {
  const [drivers, setDrivers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [tab, setTab] = useState('Info');
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [docs, setDocs] = useState([]);
  const [thumbs, setThumbs] = useState({});
  const photoInput = useRef(null);
  const docInput = useRef(null);

  // form state for new driver
  const [form, setForm] = useState({ name: '', uniqueId: '', cdlNumber: '', cdlClass: '', cdlState: '', cdlExpiry: '', hireDate: '' });
  const [newPhoto, setNewPhoto] = useState(null); // photo picked/taken before the driver exists
  const [camera, setCamera] = useState(null); // 'profile' | 'new' | null
  const [historyDevice, setHistoryDevice] = useState(null);
  const newPhotoInput = useRef(null);
  // scratch inputs
  const [empEntry, setEmpEntry] = useState({ date: '', note: '' });
  const [payEntry, setPayEntry] = useState({ date: '', amount: '', note: '' });
  const [assignTo, setAssignTo] = useState('');
  const [search, setSearch] = useState('');
  const [searchField, setSearchField] = useState('all');

  const load = async () => {
    try {
      const [drs, devs] = await Promise.all([getDrivers(), getDevices()]);
      setDrivers(drs);
      setDevices(devs);
      setError(null);
      const t = {};
      await Promise.all(drs.map(async (d) => { t[d.id] = await getDriverPhoto(d.id); }));
      setThumbs(t);
    } catch (e) { setError(e.message); }
  };
  useEffect(() => { load(); }, []);

  const driver = drivers.find((d) => d.id === openId);
  const A = (driver && driver.attributes) || {};

  // ---- search across the driver's file ----
  const searchableText = (d, field) => {
    const at = d.attributes || {};
    const cdl = at.cdl || {};
    const units = Object.values(at.assignments || {}).map((u) => u.name).join(' ');
    const parts = {
      name: d.name,
      id: d.uniqueId,
      cdl: `${cdl.number || ''} ${cdl.class || ''} ${cdl.state || ''}`,
      unit: units,
      history: [...(at.employment || []), ...(at.payments || [])].map((e) => e.note || '').join(' '),
    };
    return (field === 'all' ? Object.values(parts).join(' ') : parts[field] || '').toLowerCase();
  };

  const visibleDrivers = search.trim()
    ? drivers.filter((d) => searchableText(d, searchField).includes(search.trim().toLowerCase()))
    : drivers;

  const save = async (attrs) => {
    const updated = { ...driver, attributes: { ...A, ...attrs } };
    await updateDriver(updated);
    setDrivers((ds) => ds.map((d) => (d.id === driver.id ? updated : d)));
  };

  const openProfile = async (id) => {
    if (openId === id) { playClose(); setOpenId(null); return; }
    playOpen();
    setOpenId(id);
    setTab('Info');
    setPhoto(await getDriverPhoto(id));
    setDocs(await getDriverDocs(id));
  };
  const closeProfile = () => { playClose(); setOpenId(null); };

  // ---- add driver ----
  const addDriver = async () => {
    if (!form.name || !form.uniqueId) { alert('Name and Driver ID are required.'); return; }
    try {
      const created = await createDriver({
        name: form.name,
        uniqueId: form.uniqueId,
        attributes: {
          cdl: { number: form.cdlNumber, class: form.cdlClass, state: form.cdlState, expiry: form.cdlExpiry },
          employment: form.hireDate ? [{ date: form.hireDate, note: 'Hired' }] : [],
          payments: [],
          assignments: {},
          currentUnit: null,
          milesCompany: 0,
        },
      });
      if (newPhoto && created && created.id) await saveDriverPhoto(created.id, newPhoto);
      setAdding(false);
      setForm({ name: '', uniqueId: '', cdlNumber: '', cdlClass: '', cdlState: '', cdlExpiry: '', hireDate: '' });
      setNewPhoto(null);
      load();
    } catch (e) {
      alert(`Could not create driver: ${e.message}\n(Driver ID must be unique)`);
    }
  };

  // ---- unit assignment with cumulative per-unit logs ----
  const assignUnit = async (deviceId) => {
    const now = new Date().toISOString();
    const assignments = JSON.parse(JSON.stringify(A.assignments || {}));
    // close the open log on the current unit
    if (A.currentUnit && assignments[A.currentUnit]) {
      const logs = assignments[A.currentUnit].logs;
      const openLog = logs.find((l) => !l.end);
      if (openLog) openLog.end = now;
    }
    if (deviceId) {
      const dev = devices.find((d) => String(d.id) === String(deviceId));
      if (!assignments[deviceId]) assignments[deviceId] = { name: dev ? dev.name : `Unit ${deviceId}`, logs: [] };
      assignments[deviceId].logs.push({ start: now, end: null }); // same unit again -> appends to its existing log
    }
    await save({ assignments, currentUnit: deviceId || null });
  };

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-end', gap: '20px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h1>👤 Drivers</h1>
          <p>{error ? `🔴 ${error}` : 'Driver profiles are saved on your server. Click a driver to open their file.'}</p>
        </div>
        <button style={btnOrange} onClick={() => { playOpen(); setAdding(true); }}>+ Add Driver</button>
      </div>

      {/* search */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={{ ...inputStyle, width: '170px' }} value={searchField} onChange={(e) => setSearchField(e.target.value)}>
          <option value="all">Search everything</option>
          <option value="name">Name</option>
          <option value="id">Driver ID</option>
          <option value="cdl">CDL (number/class/state)</option>
          <option value="unit">Unit driven</option>
          <option value="history">Employment/payment notes</option>
        </select>
        <input
          style={{ ...inputStyle, flex: 1, minWidth: '220px' }}
          placeholder="🔍 Type to search drivers…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <>
            <span style={{ fontSize: '12px', color: '#64748b' }}>{visibleDrivers.length} match(es)</span>
            <button style={{ background: '#e2e8f0', color: '#334155', padding: '7px 12px', borderRadius: '6px', fontWeight: 600, fontSize: '12px' }} onClick={() => setSearch('')}>Clear</button>
          </>
        )}
      </div>

      {/* driver icons */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 22px' }}>
        {visibleDrivers.map((d) => (
          <div key={d.id} onClick={() => openProfile(d.id)} style={{ width: '108px', textAlign: 'center', cursor: 'pointer', padding: '8px 4px' }}>
            <div style={{
              width: '84px', height: '84px', margin: '0 auto 8px', borderRadius: '50%',
              background: thumbs[d.id] ? `url(${thumbs[d.id]}) center/cover` : 'linear-gradient(135deg, #f97316, #f4511e)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '30px', color: 'white',
              fontFamily: 'Poppins, sans-serif', fontWeight: 700,
              boxShadow: '0 5px 14px rgba(15,23,42,0.22)', border: '2px solid white',
            }}>
              {!thumbs[d.id] && d.name.charAt(0).toUpperCase()}
            </div>
            <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>{d.name}</div>
            <div style={{ fontSize: '10px', color: '#64748b' }}>ID: {d.uniqueId}</div>
          </div>
        ))}
        {drivers.length === 0 && !error && <p className="text-muted">No drivers yet — click "+ Add Driver".</p>}
        {drivers.length > 0 && visibleDrivers.length === 0 && <p className="text-muted">No drivers match "{search}".</p>}
      </div>

      {/* ---- add driver popup ---- */}
      {adding && (
        <div onClick={() => { playClose(); setAdding(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '480px', padding: '24px' }}>
            <h2 style={{ margin: '0 0 16px', fontFamily: 'Poppins, sans-serif', fontSize: '18px' }}>New Driver</h2>

            {/* photo: upload or take with the camera */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%', flexShrink: 0,
                background: newPhoto ? `url(${newPhoto}) center/cover` : '#f1f5f9',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px',
                border: '2px dashed #cbd5e1',
              }}>
                {!newPhoto && '👤'}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button style={{ background: '#e2e8f0', color: '#334155', padding: '7px 14px', borderRadius: '6px', fontWeight: 600, fontSize: '12px' }} onClick={() => newPhotoInput.current.click()}>⬆ Upload photo</button>
                <button style={{ ...btnBlue, padding: '7px 14px', fontSize: '12px' }} onClick={() => setCamera('new')}>📸 Take picture</button>
                {newPhoto && <button style={{ background: '#fef2f2', color: '#b91c1c', padding: '7px 12px', borderRadius: '6px', fontWeight: 600, fontSize: '12px' }} onClick={() => setNewPhoto(null)}>Remove</button>}
              </div>
              <input ref={newPhotoInput} type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                const f = e.target.files[0]; e.target.value = '';
                if (!f) return;
                try { setNewPhoto(await fileToResizedDataUrl(f, 500)); } catch (err) { alert(`Couldn't read that photo: ${err.message}`); }
              }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px' }}>
              <div style={{ gridColumn: '1 / -1' }}><span style={lbl}>Full name *</span><input style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><span style={lbl}>Driver ID * (your choice)</span><input style={inputStyle} placeholder="e.g. DD-001" value={form.uniqueId} onChange={(e) => setForm({ ...form, uniqueId: e.target.value })} /></div>
              <div><span style={lbl}>Hire date</span><input type="date" style={inputStyle} value={form.hireDate} onChange={(e) => setForm({ ...form, hireDate: e.target.value })} /></div>
              <div><span style={lbl}>CDL number</span><input style={inputStyle} value={form.cdlNumber} onChange={(e) => setForm({ ...form, cdlNumber: e.target.value })} /></div>
              <div><span style={lbl}>CDL class</span><input style={inputStyle} placeholder="A / B / C" value={form.cdlClass} onChange={(e) => setForm({ ...form, cdlClass: e.target.value })} /></div>
              <div><span style={lbl}>CDL state</span><input style={inputStyle} placeholder="FL" value={form.cdlState} onChange={(e) => setForm({ ...form, cdlState: e.target.value })} /></div>
              <div><span style={lbl}>CDL expiration</span><input type="date" style={inputStyle} value={form.cdlExpiry} onChange={(e) => setForm({ ...form, cdlExpiry: e.target.value })} /></div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '18px', justifyContent: 'flex-end' }}>
              <button style={{ background: '#e2e8f0', color: '#334155', padding: '8px 16px', borderRadius: '6px', fontWeight: 600 }} onClick={() => { playClose(); setAdding(false); }}>Cancel</button>
              <button style={btnBlue} onClick={addDriver}>Create Driver</button>
            </div>
          </div>
        </div>
      )}

      {historyDevice && <VehicleHistory device={historyDevice} onClose={() => setHistoryDevice(null)} />}

      {/* ---- camera capture ---- */}
      {camera && (
        <CameraModal
          onClose={() => setCamera(null)}
          onCapture={async (dataUrl) => {
            if (camera === 'new') {
              setNewPhoto(dataUrl);
            } else if (camera === 'profile' && driver) {
              await saveDriverPhoto(driver.id, dataUrl);
              setPhoto(dataUrl);
              setThumbs((t) => ({ ...t, [driver.id]: dataUrl }));
            }
          }}
        />
      )}

      {/* ---- driver profile popup ---- */}
      {driver && (
        <div onClick={closeProfile} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '720px', maxHeight: '90vh', overflowY: 'auto', padding: '24px' }}>

            {/* header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '14px' }}>
              <div
                onClick={() => photoInput.current.click()}
                title="Click to add/replace photo"
                style={{
                  width: '64px', height: '64px', borderRadius: '50%', cursor: 'pointer', flexShrink: 0,
                  background: photo ? `url(${photo}) center/cover` : 'linear-gradient(135deg, #f97316, #f4511e)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '24px',
                  fontFamily: 'Poppins, sans-serif', fontWeight: 700, border: '2px solid #e2e8f0',
                }}
              >
                {!photo && driver.name.charAt(0).toUpperCase()}
              </div>
              <input ref={photoInput} type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                const f = e.target.files[0]; e.target.value = '';
                if (!f) return;
                try {
                  const dataUrl = await fileToResizedDataUrl(f, 500);
                  await saveDriverPhoto(driver.id, dataUrl);
                  setPhoto(dataUrl);
                  setThumbs((t) => ({ ...t, [driver.id]: dataUrl }));
                } catch (err) { alert(`Couldn't read that photo: ${err.message}`); }
              }} />
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', fontSize: '20px', color: '#0f172a' }}>{driver.name}</h2>
                <div style={{ fontSize: '12px', color: '#64748b' }}>Driver ID: <b>{driver.uniqueId}</b>{A.currentUnit && devices.find((d) => String(d.id) === String(A.currentUnit)) ? ` · Assigned to ${devices.find((d) => String(d.id) === String(A.currentUnit)).name}` : ' · Unassigned'}</div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                  <button style={{ background: '#e2e8f0', color: '#334155', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '11px' }} onClick={() => photoInput.current.click()}>⬆ Upload photo</button>
                  <button style={{ ...btnBlue, padding: '4px 10px', fontSize: '11px' }} onClick={() => setCamera('profile')}>📸 Take picture</button>
                  {A.currentUnit && devices.find((dv) => String(dv.id) === String(A.currentUnit)) && (
                    <button style={{ ...btnBlue, padding: '4px 10px', fontSize: '11px' }} onClick={() => { const dv = devices.find((x) => String(x.id) === String(A.currentUnit)); setHistoryDevice({ id: dv.id, name: dv.name }); }}>📅 Unit Trip History</button>
                  )}
                </div>
              </div>
              <button onClick={closeProfile} style={{ background: '#f1f5f9', borderRadius: '50%', width: '30px', height: '30px', padding: 0, fontSize: '16px', color: '#475569' }}>×</button>
            </div>

            {/* tabs */}
            <div style={{ display: 'flex', gap: '6px', borderBottom: '2px solid #f1f5f9', marginBottom: '16px', flexWrap: 'wrap' }}>
              {TABS.map((t) => (
                <button key={t} onClick={() => setTab(t)} style={{
                  padding: '8px 14px', fontSize: '13px', fontWeight: 700, borderRadius: '8px 8px 0 0',
                  background: tab === t ? 'linear-gradient(135deg, #0066ff, #0047d0)' : 'transparent',
                  color: tab === t ? 'white' : '#64748b',
                }}>{t}</button>
              ))}
            </div>

            {/* ---- INFO ---- */}
            {tab === 'Info' && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <div><span style={lbl}>CDL number</span><input style={inputStyle} defaultValue={(A.cdl || {}).number || ''} onBlur={(e) => save({ cdl: { ...A.cdl, number: e.target.value } })} /></div>
                  <div><span style={lbl}>CDL class</span><input style={inputStyle} defaultValue={(A.cdl || {}).class || ''} onBlur={(e) => save({ cdl: { ...A.cdl, class: e.target.value } })} /></div>
                  <div><span style={lbl}>CDL state</span><input style={inputStyle} defaultValue={(A.cdl || {}).state || ''} onBlur={(e) => save({ cdl: { ...A.cdl, state: e.target.value } })} /></div>
                  <div><span style={lbl}>CDL expiration</span><input type="date" style={inputStyle} defaultValue={(A.cdl || {}).expiry || ''} onBlur={(e) => save({ cdl: { ...A.cdl, expiry: e.target.value } })} /></div>
                  <div><span style={lbl}>Medical card expiration</span><input type="date" style={inputStyle} defaultValue={A.medicalExpiry || ''} onBlur={(e) => save({ medicalExpiry: e.target.value })} /></div>
                  <div><span style={lbl}>Miles with company</span><input type="number" style={inputStyle} defaultValue={A.milesCompany || 0} onBlur={(e) => save({ milesCompany: Number(e.target.value) || 0 })} /></div>
                </div>
                {(A.cdl || {}).expiry && new Date(A.cdl.expiry) < new Date(Date.now() + 30 * 864e5) && (
                  <p style={{ marginTop: '12px', color: '#b45309', background: '#fffbeb', borderLeft: '4px solid #f59e0b', borderRadius: '6px', padding: '10px 12px', fontSize: '13px' }}>
                    ⚠️ CDL {new Date(A.cdl.expiry) < new Date() ? 'is EXPIRED' : 'expires within 30 days'} ({A.cdl.expiry})
                  </p>
                )}
                {A.medicalExpiry && new Date(A.medicalExpiry) < new Date(Date.now() + 30 * 864e5) && (
                  <p style={{ marginTop: '8px', color: '#b45309', background: '#fffbeb', borderLeft: '4px solid #f59e0b', borderRadius: '6px', padding: '10px 12px', fontSize: '13px' }}>
                    🏥 Medical card {new Date(A.medicalExpiry) < new Date() ? 'is EXPIRED' : 'expires within 30 days'} ({A.medicalExpiry})
                  </p>
                )}
                <h4 style={{ margin: '18px 0 6px', fontFamily: 'Poppins, sans-serif', fontSize: '13px' }}>🚛 Vehicles driven with the company</h4>
                {Object.keys(A.assignments || {}).length === 0
                  ? <p className="text-muted" style={{ fontSize: '13px' }}>None yet — assign a unit in the "Units & Logs" tab.</p>
                  : <p style={{ fontSize: '13px', color: '#334155' }}>{Object.values(A.assignments).map((u) => u.name).join(' · ')}</p>}
                <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '10px' }}>Fields save automatically when you click away.</p>
              </div>
            )}

            {/* ---- EMPLOYMENT ---- */}
            {tab === 'Employment' && (
              <div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
                  <input type="date" style={{ ...inputStyle, width: '150px' }} value={empEntry.date} onChange={(e) => setEmpEntry({ ...empEntry, date: e.target.value })} />
                  <input style={{ ...inputStyle, flex: 1, minWidth: '200px' }} placeholder="Event — hired, promoted, review, incident…" value={empEntry.note} onChange={(e) => setEmpEntry({ ...empEntry, note: e.target.value })} />
                  <button style={btnBlue} onClick={async () => {
                    if (!empEntry.date || !empEntry.note) return;
                    await save({ employment: [...(A.employment || []), empEntry] });
                    setEmpEntry({ date: '', note: '' });
                  }}>Add</button>
                </div>
                {(A.employment || []).slice().sort((a, b) => b.date.localeCompare(a.date)).map((e, i) => (
                  <div key={i} style={{ display: 'flex', gap: '14px', padding: '8px 4px', borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
                    <span style={{ color: '#64748b', width: '95px', flexShrink: 0 }}>{fmtDate(e.date)}</span>
                    <span style={{ color: '#0f172a' }}>{e.note}</span>
                  </div>
                ))}
                {(A.employment || []).length === 0 && <p className="text-muted" style={{ fontSize: '13px' }}>No employment records yet.</p>}
              </div>
            )}

            {/* ---- PAYMENTS ---- */}
            {tab === 'Payments' && (
              <div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
                  <input type="date" style={{ ...inputStyle, width: '150px' }} value={payEntry.date} onChange={(e) => setPayEntry({ ...payEntry, date: e.target.value })} />
                  <input type="number" style={{ ...inputStyle, width: '120px' }} placeholder="Amount $" value={payEntry.amount} onChange={(e) => setPayEntry({ ...payEntry, amount: e.target.value })} />
                  <input style={{ ...inputStyle, flex: 1, minWidth: '160px' }} placeholder="Note — weekly pay, bonus…" value={payEntry.note} onChange={(e) => setPayEntry({ ...payEntry, note: e.target.value })} />
                  <button style={btnBlue} onClick={async () => {
                    if (!payEntry.date || !payEntry.amount) return;
                    await save({ payments: [...(A.payments || []), { ...payEntry, amount: Number(payEntry.amount) }] });
                    setPayEntry({ date: '', amount: '', note: '' });
                  }}>Add</button>
                </div>
                {(A.payments || []).slice().sort((a, b) => b.date.localeCompare(a.date)).map((p, i) => (
                  <div key={i} style={{ display: 'flex', gap: '14px', padding: '8px 4px', borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
                    <span style={{ color: '#64748b', width: '95px', flexShrink: 0 }}>{fmtDate(p.date)}</span>
                    <span style={{ fontWeight: 700, color: '#15803d', width: '95px', flexShrink: 0 }}>${p.amount.toLocaleString()}</span>
                    <span style={{ color: '#334155' }}>{p.note}</span>
                  </div>
                ))}
                {(A.payments || []).length > 0 && (
                  <p style={{ fontSize: '13px', fontWeight: 700, marginTop: '10px', color: '#0f172a' }}>
                    Total paid: ${(A.payments || []).reduce((s, p) => s + p.amount, 0).toLocaleString()}
                  </p>
                )}
                {(A.payments || []).length === 0 && <p className="text-muted" style={{ fontSize: '13px' }}>No payments recorded yet.</p>}
              </div>
            )}

            {/* ---- UNITS & LOGS ---- */}
            {tab === 'Units & Logs' && (
              <div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <select style={{ ...inputStyle, width: '240px' }} value={assignTo} onChange={(e) => setAssignTo(e.target.value)}>
                    <option value="">— choose a unit —</option>
                    {devices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                  <button style={btnBlue} disabled={!assignTo} onClick={() => { assignUnit(assignTo); setAssignTo(''); }}>Assign to unit</button>
                  {A.currentUnit && <button style={btnOrange} onClick={() => assignUnit(null)}>End current assignment</button>}
                </div>
                {Object.entries(A.assignments || {}).length === 0 && <p className="text-muted" style={{ fontSize: '13px' }}>No unit history yet.</p>}
                {Object.entries(A.assignments || {}).map(([devId, unit]) => (
                  <div key={devId} style={{ marginBottom: '14px', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 14px' }}>
                    <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '14px', color: '#0f172a', marginBottom: '6px' }}>
                      🚛 {unit.name}
                      {String(A.currentUnit) === String(devId) && <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: 700, padding: '2px 9px', borderRadius: '999px', background: '#f0fdf4', color: '#15803d' }}>CURRENT</span>}
                    </div>
                    {unit.logs.slice().reverse().map((l, i) => (
                      <div key={i} style={{ fontSize: '13px', color: '#334155', padding: '3px 0', borderBottom: '1px solid #f8fafc' }}>
                        {new Date(l.start).toLocaleString()} → {l.end ? new Date(l.end).toLocaleString() : <b style={{ color: '#15803d' }}>current</b>}
                      </div>
                    ))}
                  </div>
                ))}
                <p style={{ fontSize: '12px', color: '#94a3b8' }}>Re-assigning to a unit the driver used before adds a new dated entry to that unit's existing log.</p>
              </div>
            )}

            {/* ---- DOCUMENTS ---- */}
            {tab === 'Documents' && (
              <div>
                <button style={btnBlue} onClick={() => docInput.current.click()}>⬆ Upload document</button>
                <input ref={docInput} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={async (e) => {
                  const f = e.target.files[0]; e.target.value = '';
                  if (!f) return;
                  const label = prompt('What is this document? (e.g. CDL license, Medical card, W-9)', f.name);
                  if (label == null) return;
                  try {
                    const dataUrl = await fileToDataUrl(f);
                    const next = [...docs, { label, fileName: f.name, type: f.type, dataUrl, addedAt: new Date().toISOString() }];
                    await saveDriverDocs(driver.id, next);
                    setDocs(next);
                  } catch (err) { alert(`Couldn't read the file: ${err.message}`); }
                }} />
                <div style={{ marginTop: '14px' }}>
                  {docs.map((doc, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 4px', borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
                      <span style={{ fontSize: '18px' }}>{doc.type === 'application/pdf' ? '📄' : '🖼️'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{doc.label}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{doc.fileName} · added {new Date(doc.addedAt).toLocaleDateString()}</div>
                      </div>
                      <a href={doc.dataUrl} download={doc.fileName} style={{ fontSize: '12px', fontWeight: 700, color: '#1d4ed8' }}>Download</a>
                      <button style={{ background: '#fef2f2', color: '#b91c1c', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 700 }} onClick={async () => {
                        const next = docs.filter((_, j) => j !== i);
                        await saveDriverDocs(driver.id, next);
                        setDocs(next);
                      }}>Remove</button>
                    </div>
                  ))}
                  {docs.length === 0 && <p className="text-muted" style={{ fontSize: '13px', marginTop: '10px' }}>No documents yet — upload the CDL license, medical card, etc.</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
